import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { AppHeader } from '@/components/AppHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Award, Lock, Star, Trophy, Sparkles, Target, Flame, Zap } from 'lucide-react';
import { User } from '@supabase/supabase-js';
import { ContextualNotificationPrompt } from "@/components/notifications/ContextualNotificationPrompt";

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  points: number;
  tier: string;
  category: string;
  requirement_type: string;
  requirement_value: number;
}

interface UserAchievement {
  id: string;
  achievement_id: string;
  unlocked_at: string;
  user_id: string;
}

const TIER_COLORS: Record<string, string> = {
  bronze: 'from-amber-600 to-amber-800',
  silver: 'from-slate-300 to-slate-500',
  gold: 'from-yellow-400 to-amber-500',
  platinum: 'from-violet-400 to-purple-600',
};

const TIER_BORDER: Record<string, string> = {
  bronze: 'border-amber-600/50',
  silver: 'border-slate-400/50',
  gold: 'border-yellow-500/50',
  platinum: 'border-violet-500/50',
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  protocol: <Target className="w-4 h-4" />,
  streak: <Flame className="w-4 h-4" />,
  engagement: <Zap className="w-4 h-4" />,
  wellness: <Sparkles className="w-4 h-4" />,
  therapy: <Star className="w-4 h-4" />,
  meditation: <Star className="w-4 h-4" />,
};

export default function Achievements() {
  const [user, setUser] = React.useState<User | null>(null);

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user ?? null);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  const { data: achievements } = useQuery({
    queryKey: ['all-achievements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('achievements')
        .select('*')
        .order('tier', { ascending: true })
        .order('points', { ascending: true });
      
      if (error) throw error;
      return data as Achievement[];
    },
  });

  const { data: userAchievements } = useQuery({
    queryKey: ['user-achievements', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) throw error;
      return data as UserAchievement[];
    },
    enabled: !!user,
  });

  const earnedIds = new Set(userAchievements?.map(ua => ua.achievement_id) || []);
  const totalPoints = userAchievements?.reduce((sum, ua) => {
    const achievement = achievements?.find(a => a.id === ua.achievement_id);
    return sum + (achievement?.points || 0);
  }, 0) || 0;

  const categories = [...new Set(achievements?.map(a => a.category) || [])];

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const renderAchievementCard = (achievement: Achievement, isEarned: boolean) => (
    <motion.div
      key={achievement.id}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className="relative"
    >
      <Card className={`overflow-hidden transition-all duration-300 ${
        isEarned 
          ? `border-2 ${TIER_BORDER[achievement.tier]} bg-card/80` 
          : 'border border-border/30 bg-card/40 opacity-60'
      }`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className={`relative w-14 h-14 rounded-xl flex items-center justify-center ${
              isEarned 
                ? `bg-gradient-to-br ${TIER_COLORS[achievement.tier]} shadow-lg` 
                : 'bg-muted/30'
            }`}>
              <span className="text-2xl">{achievement.icon}</span>
              {!isEarned && (
                <div className="absolute inset-0 bg-background/50 rounded-xl flex items-center justify-center">
                  <Lock className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`font-medium truncate ${isEarned ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {achievement.name}
                </h3>
                <Badge variant="outline" className="text-xs capitalize shrink-0">
                  {achievement.tier}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                {achievement.description}
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  {CATEGORY_ICONS[achievement.category] || <Star className="w-3 h-3" />}
                  <span className="capitalize">{achievement.category}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Trophy className="w-3 h-3 text-primary" />
                  <span className="text-xs font-medium text-primary">{achievement.points} pts</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onSignOut={handleSignOut} />

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/10 mb-4">
            <Award className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-light text-foreground mb-2">Your Achievements</h1>
          <p className="text-muted-foreground">Track your progress and celebrate your wins</p>
        </div>

        {/* Stats Card */}
        <Card className="mb-8 bg-gradient-to-br from-primary/10 to-accent/5 border-primary/20">
          <CardContent className="p-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-light text-primary">{earnedIds.size}</div>
                <div className="text-xs text-muted-foreground">Earned</div>
              </div>
              <div>
                <div className="text-3xl font-light text-foreground">{achievements?.length || 0}</div>
                <div className="text-xs text-muted-foreground">Total</div>
              </div>
              <div>
                <div className="text-3xl font-light text-primary">{totalPoints}</div>
                <div className="text-xs text-muted-foreground">Points</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Progress</span>
                <span>{Math.round((earnedIds.size / (achievements?.length || 1)) * 100)}%</span>
              </div>
              <Progress 
                value={(earnedIds.size / (achievements?.length || 1)) * 100} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* Achievements by Category */}
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="earned">Earned</TabsTrigger>
            {categories.map(cat => (
              <TabsTrigger key={cat} value={cat} className="capitalize">
                {cat}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {achievements?.map(achievement => 
                renderAchievementCard(achievement, earnedIds.has(achievement.id))
              )}
            </div>
          </TabsContent>

          <TabsContent value="earned" className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {achievements?.filter(a => earnedIds.has(a.id)).map(achievement => 
                renderAchievementCard(achievement, true)
              )}
              {earnedIds.size === 0 && (
                <div className="col-span-2 text-center py-12 text-muted-foreground">
                  <Trophy className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No achievements earned yet.</p>
                  <p className="text-sm">Keep using Therappi to unlock badges!</p>
                </div>
              )}
            </div>
          </TabsContent>

          {categories.map(cat => (
            <TabsContent key={cat} value={cat} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {achievements?.filter(a => a.category === cat).map(achievement => 
                  renderAchievementCard(achievement, earnedIds.has(achievement.id))
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Contextual notification prompt */}
        <ContextualNotificationPrompt context="achievement" className="mt-6" />
      </main>
    </div>
  );
}
